import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, FileText, Calendar, Truck, Package, Filter, 
  ChevronDown, ChevronRight, Eye, Trash2, Plus, Search,
  TrendingUp, TrendingDown, Clock, CheckCircle, Upload, Camera, Download,
  Loader2, Image as ImageIcon, X as XIcon, Pencil
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import FornitoreSelect from "../../components/FornitoreSelect";
import ImportFattureSDI from "./ImportFattureSDI";
import { useConfirm } from "../../hooks/useConfirm";

const API = process.env.REACT_APP_BACKEND_URL;

const MESI_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const StoricoCarichi = () => {
  const { localeCorrente, token } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data
  const [loading, setLoading] = useState(true);
  const [storicoCarichi, setStoricoCarichi] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [tipologieFornitori, setTipologieFornitori] = useState([]);

  // Upload fattura dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFornitore, setUploadFornitore] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // Filtri — inizializzati da URL query params (deep-link da Fornitori)
  const [showFilters, setShowFilters] = useState(false);
  const [filterFornitoreIds, setFilterFornitoreIds] = useState(() => {
    const f = searchParams.get("fornitore_id") || searchParams.get("fornitore_ids") || "";
    return f ? f.split(",").filter(Boolean) : [];
  });
  const [filterDataInizio, setFilterDataInizio] = useState(searchParams.get("dal") || "");
  const [filterDataFine, setFilterDataFine] = useState(searchParams.get("al") || "");
  const [filterTipo, setFilterTipo] = useState("");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  
  // Accordion: anno > mese
  const [annoAperto, setAnnoAperto] = useState(() => String(new Date().getFullYear()));
  const [meseAperto, setMeseAperto] = useState(() => String(new Date().getMonth()));
  const [expandedItems, setExpandedItems] = useState({});
  const [activeMainTab, setActiveMainTab] = useState("storico");

  // Auto-apri filter bar se arrivo da deep-link con filtri attivi
  useEffect(() => {
    const hasDeepLinkFilters = searchParams.get("fornitore_id") || searchParams.get("dal") || searchParams.get("al") || searchParams.get("q");
    if (hasDeepLinkFilters) setShowFilters(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modifica riga dialog
  const [editRigaDialog, setEditRigaDialog] = useState(null); // {item, rigaIdx, riga, totaleRigaFisso}
  const [editForm, setEditForm] = useState({ quantita: 0, unita_misura: "kg" });
  const [savingRiga, setSavingRiga] = useState(false);

  // Sincronizza filtri attivi → URL query params
  useEffect(() => {
    const sp = new URLSearchParams();
    if (filterFornitoreIds.length > 0) sp.set("fornitore_id", filterFornitoreIds.join(","));
    if (filterDataInizio) sp.set("dal", filterDataInizio);
    if (filterDataFine) sp.set("al", filterDataFine);
    if (searchTerm) sp.set("q", searchTerm);
    setSearchParams(sp, { replace: true });
  }, [filterFornitoreIds, filterDataInizio, filterDataFine, searchTerm, setSearchParams]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const localeParam = `locale_id=${localeCorrente.id}`;
      
      const [storicoRes, fattureRes, fornitoriRes, tipologieRes] = await Promise.all([
        fetch(`${API}/api/storico-carichi?${localeParam}&limit=500`, { headers }),
        fetch(`${API}/api/fatture?${localeParam}`, { headers }),
        fetch(`${API}/api/fornitori`, { headers }),
        fetch(`${API}/api/tipologie-fornitore`, { headers })
      ]);
      
      if (storicoRes.ok) setStoricoCarichi(await storicoRes.json());
      if (fattureRes.ok) setFatture(await fattureRes.json());
      if (fornitoriRes.ok) {
        const fornitoriData = await fornitoriRes.json();
        setFornitori(fornitoriData.sort((a, b) => 
          (a.ragione_sociale || '').localeCompare(b.ragione_sociale || '', 'it')
        ));
      }
      if (tipologieRes.ok) setTipologieFornitori(await tipologieRes.json());
    } catch (error) {
      console.error("Errore fetch:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Upload fattura OCR
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadFornitore) {
      toast.error("Seleziona prima un fornitore");
      return;
    }
    setUploading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(",")[1];
        try {
          toast.info("Analisi OCR in corso...");
          const ocrRes = await axios.post(`${API}/api/ocr/fattura`, {
            image_base64: base64,
            fornitore_id: uploadFornitore
          }, { headers });

          const fatturaData = {
            fornitore_id: uploadFornitore,
            data_fattura: ocrRes.data.data_fattura || new Date().toISOString().split("T")[0],
            righe: ocrRes.data.righe || [],
            locale_id: localeCorrente?.id,
            tipo: "food"
          };
          const fatturaRes = await axios.post(`${API}/api/fatture`, fatturaData, { headers });
          
          try {
            await axios.post(`${API}/api/fatture/${fatturaRes.data.id}/immagine`, {
              image_base64: reader.result
            }, { headers });
          } catch { /* salvataggio immagine opzionale */ }
          
          toast.success("Fattura creata — verifica i dati estratti");
          setUploading(false);
          setUploadDialogOpen(false);
          navigate(`/acquisti/fattura/${fatturaRes.data.id}/revisione`);
        } catch (ocrError) {
          // OCR fallito: crea fattura vuota per inserimento manuale
          const fatturaData = {
            fornitore_id: uploadFornitore,
            data_fattura: new Date().toISOString().split("T")[0],
            righe: [],
            locale_id: localeCorrente?.id,
            tipo: "food"
          };
          const fatturaRes = await axios.post(`${API}/api/fatture`, fatturaData, { headers });
          try {
            await axios.post(`${API}/api/fatture/${fatturaRes.data.id}/immagine`, {
              image_base64: reader.result
            }, { headers });
          } catch { /* salvataggio immagine opzionale */ }
          toast.warning("OCR non riuscito — inserimento manuale richiesto");
          setUploading(false);
          setUploadDialogOpen(false);
          navigate(`/acquisti/fattura/${fatturaRes.data.id}/revisione`);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Errore upload:", error);
      toast.error("Errore nel caricamento");
      setUploading(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Combina storico_carichi e fatture in lista unificata
  const getListaUnificata = () => {
    const items = [];
    
    // Aggiungi fatture
    fatture.forEach(f => {
      items.push({
        id: f.id,
        tipo: "fattura",
        data: f.data_fattura || f.created_at?.split("T")[0],
        fornitore_id: f.fornitore_id,
        fornitore_nome: fornitori.find(fn => fn.id === f.fornitore_id)?.ragione_sociale || "Sconosciuto",
        totale: f.righe?.reduce((sum, r) => sum + (r.prezzo_unitario || 0) * (r.quantita || 0), 0) || 0,
        num_prodotti: f.righe?.length || 0,
        stato: f.stato,
        righe: f.righe || [],
        created_at: f.created_at
      });
    });
    
    // Aggiungi storico carichi manuali
    storicoCarichi.forEach(sc => {
      // Evita duplicati se già presente nelle fatture
      if (!items.some(i => i.id === sc.fattura_id)) {
        items.push({
          id: sc.id,
          tipo: sc.tipo,
          fonte: sc.fonte,  // preserva 'sdi_import' per i carichi importati da SDI
          data: sc.data,
          fornitore_id: sc.fornitore_id,
          fornitore_nome: sc.fornitore_nome || fornitori.find(fn => fn.id === sc.fornitore_id)?.ragione_sociale || "Sconosciuto",
          totale: sc.totale,
          num_prodotti: sc.num_prodotti,
          stato: "confirmed",
          righe: sc.righe || [],
          note: sc.note,
          utente_nome: sc.utente_nome,
          created_at: sc.created_at
        });
      }
    });
    
    // Ordina per data decrescente
    items.sort((a, b) => new Date(b.data) - new Date(a.data));
    
    return items;
  };

  // Applica filtri
  const listaFiltrata = getListaUnificata().filter(item => {
    if (filterFornitoreIds.length > 0 && !filterFornitoreIds.includes(item.fornitore_id)) return false;
    if (filterDataInizio && item.data < filterDataInizio) return false;
    if (filterDataFine && item.data > filterDataFine) return false;
    if (filterTipo && item.tipo !== filterTipo) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchFornitore = item.fornitore_nome?.toLowerCase().includes(search);
      const matchProdotti = item.righe?.some(r => 
        (r.ingrediente_nome || r.prodotto_nome || r.descrizione || "").toLowerCase().includes(search)
      );
      if (!matchFornitore && !matchProdotti) return false;
    }
    return true;
  });

  // Toggle expanded
  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Raggruppa per Anno → Mese
  const raggruppaAnnoMese = useMemo(() => {
    const tree = {}; // { "2026": { "0": [item, ...], "1": [...] }, ... }
    listaFiltrata.forEach(item => {
      const date = new Date(item.data);
      if (isNaN(date.getTime())) return;
      const anno = String(date.getFullYear());
      const mese = String(date.getMonth());
      if (!tree[anno]) tree[anno] = {};
      if (!tree[anno][mese]) tree[anno][mese] = [];
      tree[anno][mese].push(item);
    });
    return tree;
  }, [listaFiltrata]);

  // Calcola prezzo per unità di misura standard (€/kg, €/lt, €/pz)
  // - Se UM = g → prezzo_unitario è €/g → moltiplica per 1000 per €/kg
  // - Se UM = ml → prezzo_unitario è €/ml → moltiplica per 1000 per €/lt
  // - Altrimenti il prezzo unitario è già €/UM nativa (kg, lt, pz, ...)
  const getPrezzoPerUnita = (riga) => {
    const pu = parseFloat(riga.prezzo_unitario || riga.costo || 0);
    if (!pu || pu <= 0) return null;
    const um = (riga.unita_misura || "kg").toLowerCase();
    if (um === "g") return { valore: pu * 1000, um: "kg" };
    if (um === "ml") return { valore: pu * 1000, um: "lt" };
    return { valore: pu, um };
  };

  // Apri dialog modifica riga
  const apriEditRiga = (item, rigaIdx, riga) => {
    // Totale riga FISSO: prendi prima dai campi espliciti (SDI), poi calcola
    const totaleRigaFisso = parseFloat(
      riga.totale ?? riga.prezzo_totale ?? (parseFloat(riga.quantita || 0) * parseFloat(riga.prezzo_unitario || riga.costo || 0))
    ) || 0;
    setEditRigaDialog({ item, rigaIdx, riga, totaleRigaFisso });
    setEditForm({
      quantita: parseFloat(riga.quantita || 0),
      unita_misura: (riga.unita_misura || "kg").toLowerCase(),
    });
  };

  // Salva modifica riga
  const salvaEditRiga = async () => {
    if (!editRigaDialog) return;
    const { item, rigaIdx } = editRigaDialog;
    if (editForm.quantita <= 0) {
      toast.error("La quantità deve essere maggiore di zero");
      return;
    }
    setSavingRiga(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(
        `${API}/api/storico-carichi/${item.id}/riga/${rigaIdx}`,
        {
          quantita: editForm.quantita,
          unita_misura: editForm.unita_misura,
        },
        { headers }
      );
      toast.success("Carico aggiornato — storico prezzi propagato");
      setEditRigaDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSavingRiga(false);
    }
  };

  // Elimina carico
  const handleDelete = async (item) => {
    if (!await confirm("Sei sicuro di voler eliminare questo carico?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = item.tipo === "fattura" 
        ? `${API}/api/fatture/${item.id}`
        : `${API}/api/storico-carichi/${item.id}`;
      
      const res = await fetch(url, { method: "DELETE", headers });
      if (res.ok) {
        toast.success("Carico eliminato");
        fetchData();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Statistiche
  const stats = {
    totaleCarichi: listaFiltrata.length,
    totaleFatture: listaFiltrata.filter(i => i.tipo === "fattura").length,
    totaleManuali: listaFiltrata.filter(i => i.tipo === "manuale").length,
    totaleSpeso: listaFiltrata.reduce((sum, i) => sum + (i.totale || 0), 0)
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  const gruppedItems = raggruppaAnnoMese;

  return (
    <div className="space-y-6" data-testid="fatture-acquisto-page">
      {/* Header */}
      <div className="hidden md:block">
        <h1 className="text-3xl font-bold text-foreground">Fatture d'acquisto</h1>
        <p className="text-muted-foreground mt-1">Lista unificata di tutti gli acquisti</p>
      </div>

      {/* Tab selector — stile Ordini/Prodotti */}
      <div className="flex gap-2">
        {[
          { key: "storico", label: "Storico Carichi", icon: FileText },
          { key: "import", label: "Import da Provider", icon: Download },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveMainTab(key)}
            data-testid={`tab-main-${key}`}
            className={`h-14 px-5 rounded-2xl font-semibold flex items-center gap-2 transition-all text-sm ${
              activeMainTab === key
                ? "bg-primary text-white shadow-sm"
                : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Tab: Storico */}
      {activeMainTab === "storico" && <div className="space-y-4">
          {/* Filtri toggle */}
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-11 px-4 rounded-2xl text-sm font-medium border transition-all flex items-center gap-2 ${
                showFilters
                  ? "bg-primary text-white border-primary"
                  : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtri</span>
            </button>
          </div>

      {/* Pulsante Carica Fattura */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Camera className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-foreground">Carica nuova fattura</p>
              <p className="text-sm text-muted-foreground">Scatta foto o carica immagine/PDF</p>
            </div>
          </div>
          <Button
            data-testid="carica-fattura-btn"
            onClick={() => setUploadDialogOpen(true)}
            className="w-full sm:w-auto h-11 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white"
          >
            <Upload className="h-4 w-4 mr-2" />
            Carica Fattura
          </Button>
        </div>
      </div>

      {/* Dialog Upload Fattura OCR */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) setUploadFornitore(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-500" />
              Carica Fattura
            </DialogTitle>
            <DialogDescription>
              Seleziona il fornitore e carica la foto o immagine della fattura. L'OCR estrarrà automaticamente i dati.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Fornitore *</Label>
              <FornitoreSelect
                fornitori={fornitori}
                tipologie={tipologieFornitori}
                value={uploadFornitore || "__none__"}
                onChange={(v) => setUploadFornitore(v === "__none__" ? "" : v)}
                placeholder="Seleziona fornitore..."
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Foto / Immagine fattura</Label>
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  uploadFornitore
                    ? "border-blue-300 cursor-pointer hover:bg-blue-50/50"
                    : "border-muted opacity-50 cursor-not-allowed"
                }`}
                onClick={() => uploadFornitore && fileInputRef.current?.click()}
              >
                <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-foreground">Clicca per caricare</p>
                <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP, PDF</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input-fattura"
              />
            </div>

            {!uploadFornitore && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Seleziona prima un fornitore per abilitare il caricamento.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialogOpen(false); setUploadFornitore(""); }}>
              Annulla
            </Button>
            <Button
              data-testid="avvia-upload-btn"
              disabled={!uploadFornitore || uploading}
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analisi in corso...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Seleziona file</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { val: stats.totaleCarichi, label: "Totale Carichi", color: "text-foreground" },
          { val: stats.totaleFatture, label: "Fatture OCR", color: "text-blue-500" },
          { val: stats.totaleManuali, label: "Inserimenti Manuali", color: "text-emerald-500" },
          { val: `€${stats.totaleSpeso.toFixed(2)}`, label: "Totale Speso", color: "text-primary" },
        ].map((s, i) => (
          <div key={i} className="bg-muted/30 border border-border rounded-2xl p-4 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
            <div className="text-sm text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filtri */}
      {showFilters && (
        <div className="bg-muted/30 border border-border rounded-2xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative md:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca fornitore o prodotto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <DateInput value={filterDataInizio} onChange={(v) => setFilterDataInizio(v)} placeholder="Dal" />
              <DateInput value={filterDataFine} onChange={(v) => setFilterDataFine(v)} placeholder="Al" />
            </div>

            {/* Multi-select fornitori — chips */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Fornitori (selezione multipla)</Label>
              <div className="flex flex-wrap items-center gap-2">
                {filterFornitoreIds.map(fid => {
                  const f = fornitori.find(x => x.id === fid);
                  return (
                    <span key={fid} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs border border-primary/30">
                      {f?.ragione_sociale || fid}
                      <button onClick={() => setFilterFornitoreIds(filterFornitoreIds.filter(x => x !== fid))} aria-label="Rimuovi">
                        <XIcon className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
                <div className="flex-1 min-w-[200px]">
                  <FornitoreSelect
                    fornitori={fornitori.filter(f => !filterFornitoreIds.includes(f.id))}
                    tipologie={tipologieFornitori}
                    value="__none__"
                    onChange={(v) => { if (v && v !== "__none__") setFilterFornitoreIds([...filterFornitoreIds, v]); }}
                    placeholder="+ Aggiungi fornitore..."
                  />
                </div>
                {filterFornitoreIds.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setFilterFornitoreIds([])} className="h-9 text-xs text-muted-foreground">
                    Pulisci ({filterFornitoreIds.length})
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {[
                { key: "", label: "Tutti" },
                { key: "fattura", label: "Fatture", icon: FileText },
                { key: "manuale", label: "Manuali", icon: Plus },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setFilterTipo(key)}
                  className={`h-9 px-4 rounded-2xl text-sm font-medium border transition-all ${
                    filterTipo === key
                      ? "bg-primary text-white border-primary"
                      : "bg-background text-muted-foreground border-border hover:text-foreground"
                  }`}
                >
                  {Icon && <Icon className="h-3.5 w-3.5 inline mr-1.5" />}{label}
                </button>
              ))}
            </div>
        </div>
      )}

      {/* Lista carichi — accordion Anno → Mese */}
      {Object.keys(raggruppaAnnoMese).length === 0 ? (
        <div className="bg-muted/30 border border-border rounded-2xl py-12 text-center px-4">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="font-semibold text-foreground">Nessun carico trovato</p>
          <p className="text-sm text-muted-foreground mt-1">
            Carica una fattura o inserisci un acquisto manuale dalla sezione Acquisti
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.keys(raggruppaAnnoMese).sort((a,b) => parseInt(b) - parseInt(a)).map(anno => {
            const mesiAnno = raggruppaAnnoMese[anno] || {};
            const totaleAnno = Object.values(mesiAnno).reduce((acc, arr) => acc + arr.reduce((s, i) => s + (i.totale || 0), 0), 0);
            const numCarichiAnno = Object.values(mesiAnno).reduce((acc, arr) => acc + arr.length, 0);
            const annoOpen = annoAperto === anno;
            return (
              <div key={anno} className="bg-card border border-border rounded-2xl overflow-hidden">
                {/* Header anno */}
                <button
                  onClick={() => setAnnoAperto(annoOpen ? null : anno)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition-colors"
                  data-testid={`accordion-anno-${anno}`}
                >
                  <div className="flex items-center gap-3">
                    {annoOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                    <span className="text-xl font-bold text-foreground">{anno}</span>
                    <span className="text-xs text-muted-foreground">{numCarichiAnno} {numCarichiAnno === 1 ? "carico" : "carichi"}</span>
                  </div>
                  <span className="text-sm font-semibold text-primary">€{totaleAnno.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </button>

                {annoOpen && (
                  <div className="border-t border-border bg-muted/20">
                    {Object.keys(mesiAnno).sort((a,b) => parseInt(b) - parseInt(a)).map(mese => {
                      const items = mesiAnno[mese];
                      const meseKey = `${anno}-${mese}`;
                      const meseOpen = meseAperto === mese && annoAperto === anno;
                      const totaleMese = items.reduce((s, i) => s + (i.totale || 0), 0);
                      return (
                        <div key={meseKey} className="border-b border-border last:border-0">
                          <button
                            onClick={() => setMeseAperto(meseOpen ? null : mese)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                            data-testid={`accordion-mese-${meseKey}`}
                          >
                            <div className="flex items-center gap-3">
                              {meseOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                              <span className="font-semibold text-foreground">{MESI_IT[parseInt(mese)]}</span>
                              <span className="text-xs text-muted-foreground">· {items.length} {items.length === 1 ? "carico" : "carichi"}</span>
                            </div>
                            <span className="text-sm font-medium text-muted-foreground">€{totaleMese.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </button>

                          {meseOpen && (
                            <div className="px-2 sm:px-4 pb-3 space-y-2">
                              {items.map(item => {
                  const isExpanded = expandedItems[item.id];
                  const isFattura = item.tipo === "fattura";
                  const isConfirmed = item.stato === "confirmed" || item.stato === "confermata";
                  const isSDI = item.fonte === "sdi_import";
                  
                  return (
                    <div
                      key={item.id}
                      className={`bg-card border rounded-2xl overflow-hidden ${!isConfirmed ? "border-amber-500/30" : "border-border"}`}
                    >
                      <div 
                        className={`p-4 ${!isConfirmed ? 'pb-2' : ''}`}
                        onClick={() => !isConfirmed ? null : toggleExpanded(item.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg flex-shrink-0 ${
                              isFattura 
                                ? "bg-blue-500/10 text-blue-500" 
                                : "bg-emerald-500/10 text-emerald-500"
                            }`}>
                              {isFattura ? <FileText className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-foreground">{item.fornitore_nome}</p>
                                {!isConfirmed && (
                                  <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-xs">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Da confermare
                                  </Badge>
                                )}
                                {isSDI && (
                                  <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/30 text-xs">SDI</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {new Date(item.data).toLocaleDateString("it-IT")}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Package className="h-3.5 w-3.5" />
                                  {item.num_prodotti} prodotti
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-foreground">€{item.totale.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {isSDI ? "SDI" : isFattura ? "Fattura" : "Manuale"}
                            </p>
                          </div>
                        </div>
                        
                        {/* Pulsanti per fatture non confermate */}
                        {!isConfirmed && (
                          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/acquisti/fattura/${item.id}/revisione`);
                              }}
                              className="flex-1 text-blue-600 border-blue-300"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Revisiona
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item);
                              }}
                              className="flex-1 text-red-500 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Elimina
                            </Button>
                          </div>
                        )}
                        
                        {/* Pulsanti per fatture confermate */}
                        {isConfirmed && (
                          <div className="flex justify-end mt-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(item.id);
                              }}
                              className="text-muted-foreground"
                              data-testid={`toggle-dettagli-${item.id}`}
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              <span className="ml-1 text-xs">{isExpanded ? 'Chiudi' : 'Dettagli'}</span>
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Dettaglio righe */}
                      {isExpanded && item.righe && item.righe.length > 0 && (
                        <div className="border-t border-border bg-muted/20 p-3 sm:p-4">
                          <div className="space-y-2">
                            {item.righe.map((riga, idx) => {
                              const ppu = getPrezzoPerUnita(riga);
                              const totaleRiga = (parseFloat(riga.prezzo_unitario || riga.costo || 0) * parseFloat(riga.quantita || 0)) || 0;
                              return (
                              <div 
                                key={idx} 
                                className="p-3 bg-background rounded-xl flex items-start justify-between gap-3"
                                data-testid={`riga-${item.id}-${idx}`}
                              >
                                <div className="flex items-start gap-2 min-w-0 flex-1">
                                  <Package className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                      {riga.ingrediente_nome || riga.prodotto_nome || riga.descrizione || "Prodotto"}
                                    </p>
                                    {riga.codice_articolo && (
                                      <p className="text-[11px] text-muted-foreground">Cod: {riga.codice_articolo}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs">
                                      <span className="text-muted-foreground">
                                        {parseFloat(riga.quantita || 0).toLocaleString("it-IT", { maximumFractionDigits: 3 })} {riga.unita_misura || "kg"}
                                      </span>
                                      {ppu && (
                                        <span className="text-primary font-medium">
                                          €{ppu.valore.toFixed(2)}/{ppu.um}
                                        </span>
                                      )}
                                      {riga.modificato_manualmente && (
                                        <Badge variant="outline" className="text-[10px] h-4 px-1 bg-amber-500/10 text-amber-600 border-amber-500/30">Modificato</Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-sm font-semibold text-foreground">€{totaleRiga.toFixed(2)}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); apriEditRiga(item, idx, riga); }}
                                    className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                                    aria-label="Modifica riga"
                                    data-testid={`edit-riga-${item.id}-${idx}`}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>}

      {/* Tab: Import da Provider */}
      {activeMainTab === "import" && (
        <ImportFattureSDI embedded />
      )}

      {/* ===== DIALOG MODIFICA RIGA CARICO ===== */}
      <Dialog open={!!editRigaDialog} onOpenChange={(o) => !o && setEditRigaDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Modifica carico interno
            </DialogTitle>
            <DialogDescription>
              {editRigaDialog?.riga?.ingrediente_nome || editRigaDialog?.riga?.prodotto_nome || editRigaDialog?.riga?.descrizione || "Prodotto"}
              <br />
              <span className="text-xs text-amber-600">Il totale della riga è IMMUTATO (da fattura). Cambia solo quantità e/o UM — il prezzo unitario si ricalcola automaticamente.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Quantità *</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={editForm.quantita}
                  onChange={(e) => setEditForm(prev => ({ ...prev, quantita: parseFloat(e.target.value) || 0 }))}
                  className="h-11 rounded-xl"
                  data-testid="edit-riga-quantita"
                />
              </div>
              <div>
                <Label className="text-xs">Unità di misura</Label>
                <select
                  value={editForm.unita_misura}
                  onChange={(e) => setEditForm(prev => ({ ...prev, unita_misura: e.target.value }))}
                  className="h-11 rounded-xl border border-border bg-background w-full px-3 text-sm"
                  data-testid="edit-riga-um"
                >
                  {["kg","g","lt","ml","pz","cf","cf24","cf12","cf6","cartone","scatola","bottiglia"].map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                Prezzo unitario (€/{editForm.unita_misura || "kg"})
                <span className="text-[10px] text-muted-foreground italic">— calcolato automaticamente</span>
              </Label>
              <div className="h-11 rounded-xl bg-muted/60 border border-border px-3 flex items-center text-sm font-semibold text-foreground" data-testid="edit-riga-prezzo-calc">
                €{(editForm.quantita > 0 ? (editRigaDialog?.totaleRigaFisso || 0) / editForm.quantita : 0).toFixed(4)}/{editForm.unita_misura || "kg"}
              </div>
            </div>
            <div className="bg-muted/40 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Totale riga</span>
                <strong>€{(editRigaDialog?.totaleRigaFisso || 0).toFixed(2)} <em className="text-[10px] font-normal text-muted-foreground">(immutato)</em></strong>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Totale fattura</span>
                <span className="text-muted-foreground">€{editRigaDialog?.item?.totale?.toFixed?.(2) || "0.00"} <em className="text-[10px]">(immutato)</em></span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRigaDialog(null)}>Annulla</Button>
            <Button onClick={salvaEditRiga} disabled={savingRiga} data-testid="salva-edit-riga">
              {savingRiga ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Salva e propaga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoricoCarichi;
